import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Award,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const ResidentPhases = ({ residentData, organizationId }) => {
  const [phaseData, setPhaseData] = useState(null);
  const [completedPhases, setCompletedPhases] = useState([]);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Toggle expanded state for a completed phase
  const toggleExpand = (phaseId) => {
    setExpandedPhases((prev) => ({
      ...prev,
      [phaseId]: !prev[phaseId],
    }));
  };

  useEffect(() => {
    const fetchPhaseData = async () => {
      if (!residentData || !organizationId) {
        setLoading(false);
        return;
      }

      try {
        // Get the resident's current phase name
        const currentPhaseName = residentData.phase;

        if (!currentPhaseName) {
          // Handle case where no phase is assigned
          setPhaseData(null);
          setLoading(false);
          return;
        }

        // Query all phases to get both current and previous phases
        const phasesRef = collection(
          db,
          `organizations/${organizationId}/phases`
        );
        const phasesSnapshot = await getDocs(
          query(phasesRef, orderBy("order", "asc"))
        );

        // Find the current phase and any phases that come before it in order
        const allPhases = phasesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Find the current phase
        const currentPhase = allPhases.find(
          (phase) => phase.name === currentPhaseName
        );

        if (!currentPhase) {
          setError(`Phase "${currentPhaseName}" not found`);
          setLoading(false);
          return;
        }

        // Get all phases with order less than current phase's order
        const previousPhases = allPhases.filter(
          (phase) => phase.order < currentPhase.order
        );

        setPhaseData(currentPhase);
        setCompletedPhases(previousPhases);

        // Initialize expanded state - all collapsed by default
        const initialExpandedState = {};
        previousPhases.forEach((phase) => {
          initialExpandedState[phase.id] = false;
        });
        setExpandedPhases(initialExpandedState);
      } catch (err) {
        console.error("Error fetching phase data:", err);
        setError("Error loading phase information");
      } finally {
        setLoading(false);
      }
    };

    fetchPhaseData();
  }, [residentData, organizationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
        <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
        <p className="text-red-300">{error}</p>
      </div>
    );
  }

  if (!phaseData) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-8 text-center">
        <div className="bg-blue-500/10 rounded-full p-4 w-16 h-16 mx-auto mb-4">
          <Clock className="text-blue-400 mx-auto" size={32} />
        </div>
        <h3 className="text-white text-xl font-semibold mb-2">
          No Phase Assigned Yet
        </h3>
        <p className="text-slate-400 max-w-lg mx-auto">
          You haven't been assigned to a phase yet. Your staff will assign you
          to a phase based on your progress in the program.
        </p>
      </div>
    );
  }

  // Helper function to render a requirement
  const renderRequirement = (req) => (
    <div
      key={req.id}
      className="bg-slate-800/70 rounded-xl p-5 border border-slate-700/70"
    >
      <div className="flex items-center gap-4">
        <div className="bg-slate-700/60 rounded-full p-3 flex-shrink-0">
          <CheckCircle className="text-indigo-400" size={20} />
        </div>
        <div className="flex-1">
          <h4 className="text-white font-medium text-lg">{req.description}</h4>

          {/* Only show frequency badges for recurring requirements (not "total" or one-time) */}
          {req.frequency !== "total" && (
            <div className="flex flex-wrap gap-3 mt-2">
              {req.frequency !== "total" && (
                <span className="px-4 py-1.5 bg-indigo-500/20 rounded-full text-sm text-indigo-300">
                  {req.count}{" "}
                  {req.count > 1
                    ? req.frequency === "weekly"
                      ? "times per week"
                      : req.frequency === "monthly"
                      ? "times per month"
                      : req.frequency === "daily"
                      ? "times per day"
                      : "times"
                    : "time"}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Current Phase Header */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-500/10 rounded-full p-2">
              <Award className="text-indigo-400" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {phaseData.name || `Phase ${phaseData.order}`}
            </h2>
          </div>
          <div className="bg-indigo-500/10 rounded-xl border border-indigo-500/20 px-6 py-3">
            <p className="text-indigo-400 font-medium">Current Phase</p>
          </div>
        </div>
      </div>

      {/* Current Phase Requirements List */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-6">
        <h3 className="text-xl font-semibold text-white mb-6">Requirements</h3>

        {phaseData.requirements && phaseData.requirements.length > 0 ? (
          <div className="space-y-5">
            {phaseData.requirements.map(renderRequirement)}
          </div>
        ) : (
          <div className="text-center py-6 bg-slate-700/20 rounded-xl">
            <p className="text-slate-400">
              No requirements specified for this phase
            </p>
          </div>
        )}
      </div>

      {/* Completed Phases Section */}
      {completedPhases.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-6">
          <h3 className="text-xl font-semibold text-white mb-6">
            Completed Phases
          </h3>

          <div className="space-y-4">
            {completedPhases.map((phase) => (
              <div
                key={phase.id}
                className="bg-slate-800/70 rounded-xl border border-slate-700/70 overflow-hidden"
              >
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => toggleExpand(phase.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-green-500/10 rounded-full p-2">
                      <CheckCircle className="text-green-400" size={20} />
                    </div>
                    <h4 className="text-white font-medium">
                      {phase.name || `Phase ${phase.order}`}
                    </h4>
                  </div>
                  {phase.requirements && phase.requirements.length > 0 && (
                    <button className="bg-slate-700/60 rounded-full p-1">
                      {expandedPhases[phase.id] ? (
                        <ChevronUp className="text-slate-400" size={18} />
                      ) : (
                        <ChevronDown className="text-slate-400" size={18} />
                      )}
                    </button>
                  )}
                </div>

                {expandedPhases[phase.id] &&
                  phase.requirements &&
                  phase.requirements.length > 0 && (
                    <div className="p-4 space-y-4 border-t border-slate-700/50">
                      {phase.requirements.map((req) => (
                        <div key={req.id} className="flex items-center gap-3">
                          <div className="bg-green-500/10 rounded-full p-2">
                            <CheckCircle className="text-green-400" size={16} />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-white">{req.description}</h4>

                            {/* Show frequency for recurring requirements in completed phases */}
                            {req.frequency !== "total" && (
                              <div className="mt-2">
                                <span className="px-4 py-1.5 bg-green-500/20 rounded-full text-sm text-green-300">
                                  {req.count}{" "}
                                  {req.count > 1
                                    ? req.frequency === "weekly"
                                      ? "times per week"
                                      : req.frequency === "monthly"
                                      ? "times per month"
                                      : req.frequency === "daily"
                                      ? "times per day"
                                      : "times"
                                    : "time"}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResidentPhases;
